/**
 * Angular Password Requirements Component
 *
 * Migrated version of the AngularJS acme-password-requirements component.
 *
 * Original: apps/b2b-ui/common/components/password-requirements/password-requirements.component.ts
 *
 * Usage in Angular templates:
 *   <app-password-requirements
 *     [password]="model.password"
 *     [passwordPolicy]="passwordPolicy">
 *   </app-password-requirements>
 *
 * Usage in AngularJS templates (after downgrade):
 *   <ng-password-requirements
 *     password="$ctrl.model.password"
 *     password-policy="$ctrl.passwordPolicy">
 *   </ng-password-requirements>
 */
import { Component, HostBinding, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { createPasswordPolicy, type PasswordPolicy } from '@acme/b2b/security';
import { passwordValidatorFactory, type PasswordRequirementsValidationErrors } from '@acme/b2b/validators';

interface PasswordRequirement {
  key: keyof PasswordRequirementsValidationErrors;
  label: string;
}

@Component({
  selector: 'app-password-requirements',
  standalone: true,
  imports: [NgFor, NgIf],
  template: require('./password-requirements.component.html'),
  styles: [require('./password-requirements.component.less')],
})
export class PasswordRequirementsComponent implements OnChanges {
  @Input() password = '';
  @Input() passwordPolicy: PasswordPolicy = createPasswordPolicy();

  validationErrors: PasswordRequirementsValidationErrors | null = null;
  requirements: PasswordRequirement[] = this.createPasswordRequirements(this.passwordPolicy);

  @HostBinding('class.acme-password-requirements') readonly baseClass = true;

  isValidMapper = (key: keyof PasswordRequirementsValidationErrors): boolean => {
    if (this.password == null || this.password === '') {
      return false;
    }

    if (this.validationErrors == null) {
      return true;
    }

    return !this.validationErrors?.[key];
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['password']) {
      const passwordValidator = passwordValidatorFactory(this.passwordPolicy);
      this.validationErrors = passwordValidator(this.password);
    }

    if (changes['passwordPolicy']) {
      this.requirements = this.createPasswordRequirements(this.passwordPolicy);
    }
  }

  createPasswordRequirements(passwordPolicy: PasswordPolicy): PasswordRequirement[] {
    const requirements: PasswordRequirement[] = [];

    if (passwordPolicy.passwordMinimumLength > 0) {
      requirements.push({
        key: 'minlength',
        label: `Be a minimum of ${passwordPolicy.passwordMinimumLength} characters`,
      });
    }

    if (passwordPolicy.passwordUseMixedCase) {
      requirements.push({
        key: 'atLeastOneLowercaseLetter',
        label: `Include at least 1 lowercase letter (a through z)`,
      });
      requirements.push({
        key: 'atLeastOneUppercaseLetter',
        label: `Include at least 1 uppercase letter (A through Z)`,
      });
    }

    if (passwordPolicy.passwordMinimumDigits > 0) {
      requirements.push({
        key: 'atLeastOneDigit',
        label: `Include at least ${passwordPolicy.passwordMinimumDigits} digit (0 through 9)`,
      });
    }

    if (passwordPolicy.passwordMinimumSymbols > 0) {
      requirements.push({
        key: 'atLeastOneNonAlphabeticCharacter',
        label: `Include at least ${passwordPolicy.passwordMinimumSymbols} non-alphabetic characters (for example !, $, #, %)`,
      });
    }

    return requirements;
  }
}
